// Lightweight wrapper around face-api.js (TinyFaceDetector) used by the
// points-watching camera to decide whether the captured frame actually
// contains a clear face. Runs entirely in the browser — no image leaves the
// device for this check.
//
// Model files must live in `public/models/` (served at `/models`):
//   - tiny_face_detector_model-weights_manifest.json
//   - tiny_face_detector_model-shard1
// Grab them from the face-api.js repo (weights/ folder). Until they're present
// detection is a safe no-op: detectFace() resolves to null and the server
// falls back to its previous "photo present = OK" behavior (no false alerts).

import * as faceapi from 'face-api.js';

const MODEL_URL = '/models';

// A face counts as "proper" either when the detector is reasonably confident
// AND the face is big enough relative to the frame, OR when the face box is so
// large that its presence is unambiguous regardless of confidence.
//
// SCORE_THRESHOLD lowered from 0.5: under the heavy colored gameroom lighting
// even clear, properly sized faces only score ~0.2–0.25 (measured on real
// captures), so a 0.5 gate false-flagged them as "no clear image". The width
// gate still rejects tiny background faces / noise at this lower score.
const SCORE_THRESHOLD = 0.2;
const MIN_FACE_WIDTH_RATIO = 0.12; // confident path: box width >= 12% of frame width
// Large-box override: a face box this wide is unmistakably the subject, so we
// accept it even when the detector's confidence is low (as long as it cleared
// the detector floor below). 0.30 ≈ a normal points-watching close-up.
const LARGE_FACE_WIDTH_RATIO = 0.3;
// Floor passed to the detector itself. detectAllFaces() discards anything below
// this, so it must sit under the low scores real faces get here — otherwise the
// large-box override never sees the candidate detection, and the confident-path
// SCORE_THRESHOLD above (0.2) does the real quality filtering.
const DETECT_SCORE_FLOOR = 0.1;
// A larger input size than the 320 default helps recall on these frames.
const INPUT_SIZE = 416;

let modelsPromise = null;
let modelsReady = false;

// Loads the TinyFaceDetector model once. Resolves true on success, false if
// the model files can't be loaded (missing/offline) so callers can degrade
// gracefully instead of throwing.
export function loadFaceModels() {
  if (modelsReady) return Promise.resolve(true);
  if (!modelsPromise) {
    modelsPromise = faceapi.nets.tinyFaceDetector
      .loadFromUri(MODEL_URL)
      .then(() => {
        modelsReady = true;
        return true;
      })
      .catch((err) => {
        // Reset so a later attempt (e.g. after files are deployed) can retry.
        modelsPromise = null;
        console.error('face-api.js models failed to load from', MODEL_URL, err);
        return false;
      });
  }
  return modelsPromise;
}

// Detects whether `input` (an HTMLCanvasElement / HTMLVideoElement / HTMLImageElement)
// contains a clear face.
//   returns true  -> a proper face was found
//   returns false -> no proper face
//   returns null  -> detection couldn't run (models missing or error); caller
//                    should treat this as "unknown" and not raise a false alert
//
// The gameroom's purple/blue lighting defeats TinyFaceDetector two different
// ways, and no single preprocessing fixes both (measured on real captures):
//   1. Brighter frames detect on a plain luminance grayscale, but break under
//      an aggressive contrast stretch (bright backgrounds blow out the face).
//   2. Very dark / deeply tinted frames vanish under luminance grayscale (the
//      0.114 blue weight crushes the blue-lit facial detail) but recover
//      strongly on a max-channel + contrast-stretched copy.
// So we run up to two passes and accept if EITHER finds a face. The second,
// more expensive pass only runs when the first turns up nothing.
export async function detectFace(input) {
  try {
    const ok = await loadFaceModels();
    if (!ok) return null;

    const imgWidth =
      input.width || input.videoWidth || input.naturalWidth || 640;

    // Pass 1 — plain luminance grayscale (neutralizes a mild color cast).
    const grayPass = await runAndAccept(
      toGrayscaleCanvas(input) || input,
      imgWidth
    );
    if (grayPass) return true;

    // Pass 2 — max-channel + percentile contrast stretch. Recovers the dark,
    // heavily-tinted frames that pass 1 can't see.
    const stretched = toMaxChannelStretchedCanvas(input);
    if (stretched) {
      const stretchPass = await runAndAccept(stretched, imgWidth);
      if (stretchPass) return true;
    }

    return false;
  } catch (err) {
    console.error('face detection error', err);
    return null;
  }
}

// Runs the detector on a prepared `target` canvas/element and applies the
// accept logic (confident path OR large-box override). Returns a boolean.
async function runAndAccept(target, imgWidth) {
  const options = new faceapi.TinyFaceDetectorOptions({
    inputSize: INPUT_SIZE,
    scoreThreshold: DETECT_SCORE_FLOOR,
  });
  const detections = await faceapi.detectAllFaces(target, options);
  if (!detections || detections.length === 0) return false;

  const minWidth = imgWidth * MIN_FACE_WIDTH_RATIO;
  const largeWidth = imgWidth * LARGE_FACE_WIDTH_RATIO;

  return detections.some((d) => {
    if (!d.box) return false;
    // Confident path: reasonably sized AND high score.
    if (d.box.width >= minWidth && d.score >= SCORE_THRESHOLD) return true;
    // Large-box override: a very wide face box is unambiguous even at the
    // low confidence the gameroom lighting produces.
    return d.box.width >= largeWidth;
  });
}

// Returns a new canvas holding the grayscale version of `input`
// (HTMLCanvasElement / HTMLVideoElement / HTMLImageElement). Returns null if a
// canvas can't be created so the caller falls back to the raw input.
function toGrayscaleCanvas(input) {
  try {
    const w = input.width || input.videoWidth || input.naturalWidth;
    const h = input.height || input.videoHeight || input.naturalHeight;
    if (!w || !h) return null;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(input, 0, 0, w, h);
    const img = ctx.getImageData(0, 0, w, h);
    const px = img.data;
    for (let i = 0; i < px.length; i += 4) {
      const l = 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
      px[i] = px[i + 1] = px[i + 2] = l;
    }
    ctx.putImageData(img, 0, 0);
    return canvas;
  } catch (err) {
    return null;
  }
}

// Returns a new canvas where each pixel is the max of its R/G/B channels,
// then contrast-stretched so the 2nd–98th percentile maps to 0–255. Taking the
// max channel (rather than a blue-suppressing luminance) preserves the facial
// detail that the purple/blue lighting pushes into the blue channel, and the
// percentile stretch rescues badly underexposed frames. Returns null on
// failure so the caller can skip this pass.
function toMaxChannelStretchedCanvas(input) {
  try {
    const w = input.width || input.videoWidth || input.naturalWidth;
    const h = input.height || input.videoHeight || input.naturalHeight;
    if (!w || !h) return null;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(input, 0, 0, w, h);
    const img = ctx.getImageData(0, 0, w, h);
    const px = img.data;
    const n = px.length / 4;

    // Per-pixel max channel.
    const m = new Uint8ClampedArray(n);
    for (let i = 0, j = 0; i < px.length; i += 4, j++) {
      let v = px[i];
      if (px[i + 1] > v) v = px[i + 1];
      if (px[i + 2] > v) v = px[i + 2];
      m[j] = v;
    }

    // Robust 2nd / 98th percentile bounds via a 256-bin histogram.
    const hist = new Int32Array(256);
    for (let j = 0; j < n; j++) hist[m[j]]++;
    const loCount = n * 0.02;
    const hiCount = n * 0.98;
    let lo = 0;
    let hi = 255;
    let acc = 0;
    for (let v = 0; v < 256; v++) {
      acc += hist[v];
      if (acc >= loCount) {
        lo = v;
        break;
      }
    }
    acc = 0;
    for (let v = 0; v < 256; v++) {
      acc += hist[v];
      if (acc >= hiCount) {
        hi = v;
        break;
      }
    }
    const range = Math.max(1, hi - lo);

    for (let i = 0, j = 0; i < px.length; i += 4, j++) {
      let s = ((m[j] - lo) / range) * 255;
      if (s < 0) s = 0;
      else if (s > 255) s = 255;
      px[i] = px[i + 1] = px[i + 2] = s;
    }
    ctx.putImageData(img, 0, 0);
    return canvas;
  } catch (err) {
    return null;
  }
}
